/**
 * SwapTemplateModal — cambia la plantilla VISUAL de un plan ya armado.
 *
 * Caso de uso: el coach armó un plan completo con plantilla "femenina rosa"
 * para Camila y ahora quiere reutilizarlo para Juan con una plantilla
 * masculina/azul. Las cantidades, ejercicios, comidas, datos del cliente NO
 * deben perderse — solo cambia el "wrap visual" (portada, paleta,
 * tipografía, theme).
 *
 * Usa `applyTemplateStyleOnly` (templates.ts) que NO ejecuta el applyDefaults
 * del template (que sobreescribiría contenido). Solo cambia los campos
 * visuales del PlanBrand + el id de template.
 *
 * Invocable desde:
 *   - PlanBuilderScreen header (botón "Cambiar plantilla")
 *   - DuplicatePlanModal (step opcional al duplicar para otro cliente)
 *
 * Layout: galería compacta de cards (similar al step 2 de CreatePlanModal)
 * con la plantilla ACTUAL marcada por default y "Sin cambios" arriba si
 * el coach se arrepiente.
 */
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../../lib/Icon";
import { fontStack } from "../../lib/fontStack";
import { applyTemplateStyleOnly, getTemplate, TEMPLATES } from "../../types/templates";
import type { PlanDoc } from "../../types/plan";
import { TemplateCover } from "./TemplateCover";

interface SwapTemplateModalProps {
  /** El plan al que se le va a cambiar la plantilla. */
  plan: PlanDoc;
  /** Llamado con el plan nuevo (referencia distinta) cuando el coach
   *  confirma. El padre persiste con updatePlan. Si el coach cancela o
   *  elige la misma plantilla actual, este callback NO se dispara. */
  onApply: (nextPlan: PlanDoc) => void;
  onClose: () => void;
}

export function SwapTemplateModal({ plan, onApply, onClose }: SwapTemplateModalProps) {
  const currentTpl = getTemplate(plan.template);
  const [selectedId, setSelectedId] = useState<string>(plan.template);
  const [q, setQ] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const list = useMemo(() => {
    const lower = q.trim().toLowerCase();
    if (!lower) return TEMPLATES;
    return TEMPLATES.filter((t) =>
      `${t.label} ${t.category} ${t.description}`.toLowerCase().includes(lower),
    );
  }, [q]);

  const handleApply = () => {
    if (selectedId === plan.template) {
      // Sin cambios — solo cerramos sin tocar el plan
      onClose();
      return;
    }
    const tpl = getTemplate(selectedId);
    if (!tpl) return;
    const next = applyTemplateStyleOnly(plan, tpl);
    onApply(next);
  };

  const isDifferent = selectedId !== plan.template;

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div
        className="al-modal al-swap-tpl"
        role="dialog"
        aria-label="Cambiar plantilla del plan"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Cambiar plantilla
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              Cambia solo la APARIENCIA (portada, colores, tipografía). El contenido
              del plan (rutina, dieta, datos del cliente) se mantiene intacto.
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="al-swap-tpl-body">
          {currentTpl && (
            <div className="al-swap-tpl-current">
              <Icon name="check" size={12} /> Plantilla actual: <b>&nbsp;{currentTpl.label}</b>
              <span className="muted">&nbsp;· {currentTpl.category}</span>
            </div>
          )}

          <div className="al-create-plan-search">
            <Icon name="search" size={14} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar plantilla — hipertrofia, definición, calistenia…"
            />
          </div>

          <div className="al-swap-grid">
            {list.map((t) => {
              const isOn = selectedId === t.id;
              const isCurrent = t.id === plan.template;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={"al-tpl-card al-swap-card" + (isOn ? " is-picked" : "")}
                  onClick={() => setSelectedId(t.id)}
                  style={{ borderColor: isOn ? t.palette.primary : undefined }}
                >
                  <div className="al-tpl-card-cover">
                    <TemplateCover template={t} size="thumb" />
                    {isOn && (
                      <span className="al-swap-card-check" style={{ background: t.palette.primary }}>
                        <Icon name="check" size={13} />
                      </span>
                    )}
                    {isCurrent && <span className="al-swap-card-actual">ACTUAL</span>}
                  </div>
                  <div className="al-tpl-card-meta">
                    <div className="al-tpl-card-name" style={{ fontFamily: fontStack(t.typography.display) }}>
                      {t.label}
                    </div>
                    <div className="al-tpl-card-cat" style={{ color: t.palette.primary }}>
                      {t.category}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="al-create-plan-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleApply}
            disabled={!isDifferent}
            title={isDifferent ? "Aplicar la nueva plantilla al plan" : "Elegí una plantilla distinta a la actual"}
          >
            {isDifferent ? "Aplicar nueva plantilla" : "Sin cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
